/**
 * One field that reaches the whole catalog: games, shelves and moods.
 *
 * It searches the words the visitor can actually see, in the language they are
 * reading, and it never pads the list - with one released game, most queries
 * return a shelf or a mood, and plenty return nothing. The empty state says so
 * in a sentence instead of showing a spinner.
 *
 * Keyboard: type to filter, Up/Down to move, Enter to open, Escape to close.
 */

import { useEffect, useId, useMemo, useRef, useState } from "react";
import { searchCatalog } from "@kikik/game-catalog";
import type { SearchHit } from "@kikik/game-catalog";
import { useI18n } from "../i18n";
import type { StringKey } from "@kikik/i18n";

export interface PortalSearchProps {
  onOpenGame: (slug: string) => void;
  onOpenCategory: (id: string) => void;
  onOpenMood: (id: string) => void;
  /** Phones show the field only after the search button is pressed. */
  onDismiss?: () => void;
  autoFocus?: boolean;
}

export function PortalSearch({
  onOpenGame,
  onOpenCategory,
  onOpenMood,
  onDismiss,
  autoFocus,
}: PortalSearchProps) {
  const { t } = useI18n();
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const [open, setOpen] = useState(false);
  const listId = useId();
  const inputRef = useRef<HTMLInputElement>(null);

  const hits = useMemo(
    () => searchCatalog(query, (key) => t(key as StringKey)),
    // `t` changes with the language, which is exactly when results must be redone.
    [query, t],
  );

  useEffect(() => setActive(0), [query]);
  useEffect(() => {
    if (autoFocus) inputRef.current?.focus();
  }, [autoFocus]);

  const kindLabel = (kind: SearchHit["kind"]): string =>
    t(`portal.search.kind.${kind}` as StringKey);

  const openHit = (hit: SearchHit | undefined): void => {
    if (!hit) return;
    setOpen(false);
    setQuery("");
    if (hit.kind === "game") onOpenGame(hit.id);
    else if (hit.kind === "category") onOpenCategory(hit.id);
    else onOpenMood(hit.id);
    onDismiss?.();
  };

  const showPanel = open && query.trim().length > 0;

  return (
    <div className="search" data-open={showPanel}>
      <label className="search__label" htmlFor={`${listId}-input`}>
        {t("portal.search.label")}
      </label>
      <div className="search__field">
        <svg className="search__glass" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
          <circle cx="10.5" cy="10.5" r="6.5" />
          <path d="M15.5 15.5 21 21" />
        </svg>
        <input
          id={`${listId}-input`}
          ref={inputRef}
          type="search"
          className="search__input"
          value={query}
          placeholder={t("portal.search.placeholder")}
          role="combobox"
          aria-expanded={showPanel}
          aria-controls={listId}
          aria-autocomplete="list"
          {...(showPanel && hits[active] ? { "aria-activedescendant": `${listId}-${active}` } : {})}
          onChange={(event) => {
            setQuery(event.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              setQuery("");
              setOpen(false);
              onDismiss?.();
              return;
            }
            if (!showPanel || hits.length === 0) return;
            if (event.key === "ArrowDown") {
              event.preventDefault();
              setActive((index) => (index + 1) % hits.length);
            } else if (event.key === "ArrowUp") {
              event.preventDefault();
              setActive((index) => (index - 1 + hits.length) % hits.length);
            } else if (event.key === "Enter") {
              event.preventDefault();
              openHit(hits[active]);
            }
          }}
        />
      </div>

      {showPanel ? (
        <div className="search__panel">
          <p className="search__count" role="status">
            {hits.length > 0
              ? t("portal.search.results", { count: hits.length, query: query.trim() })
              : t("portal.search.empty", { query: query.trim() })}
          </p>
          {hits.length > 0 ? (
            <>
              <ul className="search__list" id={listId} role="listbox" aria-label={t("portal.search.label")}>
                {hits.map((hit, index) => (
                  <li key={`${hit.kind}-${hit.id}`} role="none">
                    <button
                      type="button"
                      id={`${listId}-${index}`}
                      role="option"
                      aria-selected={index === active}
                      className={`search__hit ${index === active ? "search__hit--active" : ""}`}
                      data-kind={hit.kind}
                      onMouseEnter={() => setActive(index)}
                      onClick={() => openHit(hit)}
                    >
                      <span className="search__hit-name">{t(hit.nameKey as StringKey)}</span>
                      <span className="search__hit-kind">{kindLabel(hit.kind)}</span>
                    </button>
                  </li>
                ))}
              </ul>
              <p className="search__hint sr-only">{t("portal.search.hint")}</p>
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
